import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PremiumLock from "./PremiumLock";
import {
  ArrowsClockwise,
  CircleNotch,
  TrendUp,
  TrendDown,
  MagnifyingGlass,
  Fire,
  ArrowDown,
  Warning,
  Plug,
} from "@phosphor-icons/react";

const STALE_AFTER_MS = 5 * 60 * 1000; // 5 minutes

const BUCKETS = {
  "Huge Build-up": { chip: "bg-pos-soft text-pos border-pos-soft", row: "hsl(160 84% 39%)" },
  "Build-up": { chip: "bg-pos-soft text-pos border-pos-soft", row: "hsl(160 84% 39% / 0.6)" },
  Flat: { chip: "bg-surface-hover text-secondary-fg border-subtle", row: "hsl(var(--text-muted) / 0.3)" },
  Unwind: { chip: "bg-neg-soft text-neg border-neg-soft", row: "hsl(0 84% 60% / 0.6)" },
  "Heavy Unwind": { chip: "bg-neg-soft text-neg border-neg-soft", row: "hsl(0 84% 60%)" },
};

// 4-quadrant F&O patterns. Bullish patterns shown green, bearish red.
const PATTERNS = {
  "Long Build-up":   { chip: "bg-pos-soft text-pos border-pos-soft",     short: "Long ↑",   bias: "bullish" },
  "Short Covering":  { chip: "bg-pos-soft text-pos border-pos-soft",     short: "Cover ↑",  bias: "bullish" },
  "Short Build-up":  { chip: "bg-neg-soft text-neg border-neg-soft",     short: "Short ↓",  bias: "bearish" },
  "Long Unwinding":  { chip: "bg-neg-soft text-neg border-neg-soft",     short: "Unwind ↓", bias: "bearish" },
  Neutral:           { chip: "bg-surface-hover text-secondary-fg border-subtle", short: "—", bias: "neutral" },
};

const REFRESH_MS = 60 * 1000; // auto-refresh every 60s during market hours

const fmtInt = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : "—");
const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—");

export default function FnoOI({ onOpenStock }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("pct_change_oi"); // pct_change_oi | oi | volume
  const [dir, setDir] = useState(1); // 1 desc (high first), -1 asc (low first)
  const [fetchError, setFetchError] = useState("");
  const [locked, setLocked] = useState(false);
  const [patternFilter, setPatternFilter] = useState("all"); // all | bullish | bearish | Long Build-up | Short Build-up | Long Unwinding | Short Covering

  const load = useCallback(async (force = false) => {
    try {
      setFetchError("");
      const { data } = await api.get(`/fno/oi${force ? "?force=true" : ""}`);
      setData(data);
      setLocked(false);
    } catch (e) {
      setFetchError(e.response?.data?.detail || e.message || "Failed");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  // Auto-refresh every 60s (market hours data is near-live)
  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) {
        setRefreshing(true);
        load(true);
      }
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load(true);
  };

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const q = search.trim().toUpperCase();
    let out = data.rows.filter((r) => !q || r.symbol.includes(q));
    if (patternFilter !== "all") {
      if (patternFilter === "bullish") {
        out = out.filter((r) => r.pattern === "Long Build-up" || r.pattern === "Short Covering");
      } else if (patternFilter === "bearish") {
        out = out.filter((r) => r.pattern === "Short Build-up" || r.pattern === "Long Unwinding");
      } else {
        out = out.filter((r) => r.pattern === patternFilter);
      }
    }
    out = [...out].sort((a, b) => {
      const va = a[sortBy] ?? 0;
      const vb = b[sortBy] ?? 0;
      return dir * (vb - va);
    });
    return out;
  }, [data, search, sortBy, dir, patternFilter]);

  // Counts per pattern (across all rows, ignore search)
  const patternCounts = useMemo(() => {
    const c = { "Long Build-up": 0, "Short Build-up": 0, "Long Unwinding": 0, "Short Covering": 0, Neutral: 0 };
    (data?.rows || []).forEach((r) => {
      if (c[r.pattern] !== undefined) c[r.pattern]++;
    });
    return c;
  }, [data]);

  const buildUps = data?.rows?.filter((r) => r.pct_change_oi >= 2).length || 0;
  const unwinds = data?.rows?.filter((r) => r.pct_change_oi <= -2).length || 0;

  const toggleSort = (key) => {
    if (sortBy === key) {
      setDir(-dir);
    } else {
      setSortBy(key);
      setDir(1);
    }
  };

  const SortCell = ({ label, k, align = "right" }) => (
    <button
      type="button"
      onClick={() => toggleSort(k)}
      data-testid={`sort-${k}`}
      className={
        "flex items-center gap-1 w-full text-[10px] font-mono uppercase tracking-widest hover:text-primary-fg " +
        (align === "right" ? "justify-end" : "")
      }
    >
      {label}
      {sortBy === k && <ArrowDown size={10} className={dir === 1 ? "" : "rotate-180"} />}
    </button>
  );

  const isPremium = data?.is_premium !== false;

  if (locked) {
    return (
      <PremiumLock
        title="F&O Open Interest Scanner"
        subtitle="Live OI build-ups, unwinds and % change across all NSE F&O underlyings — powered by broker API."
        featureBullets={[
          "Real-time OI + % change in OI for 180+ F&O stocks",
          "Instantly spot build-ups (long) and unwinds (short)",
          "Auto-refresh every 5 min during market hours",
        ]}
      />
    );
  }

  if (loading) {
    return (
      <div className="p-8 border border-subtle bg-surface rounded-sm flex items-center gap-3" data-testid="fno-loading">
        <CircleNotch className="animate-spin text-mod" size={18} weight="bold" />
        <div className="text-sm text-secondary-fg">Fetching NSE F&amp;O Open Interest…</div>
      </div>
    );
  }

  const fetched = data?.fetched_at
    ? new Date(data.fetched_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "—";

  // Compute how old the snapshot is. Used to render a prominent stale
  // banner when the Upstox token is expired and the last successful fetch
  // is more than a day (or hour) old.
  const ageMs = data?.fetched_at ? Date.now() - new Date(data.fetched_at).getTime() : 0;
  const isStaleServer = !!data?.stale || !!data?.error;
  const isStaleClient = ageMs > STALE_AFTER_MS;
  const isVeryStale = ageMs > 6 * 60 * 60 * 1000; // > 6 hours = clearly broken
  const showStale = isStaleServer || isStaleClient;

  // Human-readable age string ("17 days", "3 hours", "12 mins")
  const ageLabel = (() => {
    if (!ageMs || ageMs < 0) return null;
    const days = Math.floor(ageMs / 86400000);
    const hours = Math.floor(ageMs / 3600000);
    const mins = Math.floor(ageMs / 60000);
    if (days >= 1) return `${days} day${days === 1 ? "" : "s"}`;
    if (hours >= 1) return `${hours} hour${hours === 1 ? "" : "s"}`;
    if (mins >= 1) return `${mins} min${mins === 1 ? "" : "s"}`;
    return "moments";
  })();

  // Admin-only: trigger Upstox headless auto-relogin. If auto-login isn't
  // configured on this deployment, fall back to opening the OAuth flow in a
  // new tab so the admin can approve access manually.
  const reconnectUpstox = async () => {
    if (!isAdmin) return;
    setReconnecting(true);
    try {
      const { data: r } = await api.post("/upstox/auth/auto-refresh");
      toast.success(`Upstox reconnected — ${r?.user_name || "OK"}. Refreshing F&O…`);
      setRefreshing(true);
      await load(true);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "";
      // If auto-login isn't wired up, open manual OAuth
      if (detail.toLowerCase().includes("auto-login not configured") ||
          detail.toLowerCase().includes("not configured")) {
        try {
          const { data: r2 } = await api.get("/upstox/auth/start");
          if (r2?.authorize_url) {
            window.open(r2.authorize_url, "_blank", "noopener");
            toast.info("Approve Upstox access in the new tab, then click Refresh.");
            return;
          }
        } catch (e2) {
          toast.error(e2.response?.data?.detail || "Manual Upstox OAuth also unavailable");
          return;
        }
      }
      toast.error(`Upstox reconnect failed: ${detail}`);
    } finally {
      setReconnecting(false);
    }
  };

  return (
    <div data-testid="fno-oi">
      {/* Stale / error banner */}
      {showStale && (
        <div
          className={
            "mb-4 p-3 border rounded-sm text-sm flex items-start gap-2 " +
            (isVeryStale
              ? "border-neg-soft bg-neg-soft text-neg"
              : "border-mod-soft bg-mod-soft text-mod")
          }
          data-testid="fno-stale-banner"
        >
          <Warning size={16} weight="bold" className="shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium" data-testid="fno-stale-title">
              {isVeryStale
                ? `Data is ${ageLabel} old — Upstox reconnection needed`
                : "Live fetch failed — showing last successful snapshot"}
            </div>
            <div className="text-xs text-secondary-fg mt-0.5">
              {data?.error || fetchError ||
                (isVeryStale
                  ? "Our Upstox broker token has expired. Reconnect Upstox to resume live F&O updates."
                  : "Try Refresh in a few minutes.")}
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={reconnectUpstox}
                disabled={reconnecting}
                data-testid="fno-reconnect-upstox-btn"
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest border border-pos text-pos rounded-sm hover:bg-pos-soft/20 disabled:opacity-50 transition"
              >
                {reconnecting ? (
                  <CircleNotch size={11} className="animate-spin" weight="bold" />
                ) : (
                  <Plug size={11} weight="bold" />
                )}
                {reconnecting ? "Reconnecting…" : "Reconnect Upstox"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Free preview banner */}
      {!isPremium && (
        <div
          data-testid="fno-free-banner"
          className="mb-4 p-4 border border-mod-soft bg-mod-soft rounded-sm flex items-start md:items-center justify-between gap-4 flex-col md:flex-row"
        >
          <div className="text-sm">
            <div className="font-mono text-[10px] uppercase tracking-widest text-mod mb-1">
              Free preview
            </div>
            <div className="text-primary-fg">
              Showing only <span className="font-semibold">Symbol + % Δ OI</span>. Unlock LTP, % Δ Price, OI, Pattern, F&O analytics & 60-second auto-refresh.
            </div>
          </div>
          <a
            href="/pricing"
            data-testid="fno-upgrade-btn"
            className="px-4 py-2 rounded-sm font-mono text-[10px] uppercase tracking-widest hover:opacity-90 transition shrink-0"
            style={{ background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" }}
          >
            Upgrade · ₹5 trial
          </a>
        </div>
      )}

      {/* Header strip */}
      <div className="flex items-baseline justify-between mb-4 border-b border-subtle pb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-fg">
            /// NSE F&amp;O · Open Interest
          </div>
          <div className="text-xs text-muted-fg mt-1">
            Trading date <span className="font-mono text-primary-fg">{data?.curr_trading_date || "—"}</span> · Δ
            vs <span className="font-mono text-primary-fg">{data?.prev_trading_date}</span> · fetched {fetched} · cached 5 min
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          data-testid="fno-refresh"
          className="px-3 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest hover:bg-surface-hover text-secondary-fg hover:text-primary-fg transition flex items-center gap-2"
        >
          {refreshing ? (
            <CircleNotch size={12} weight="bold" className="animate-spin" />
          ) : (
            <ArrowsClockwise size={12} weight="bold" />
          )}
          Refresh
        </button>
      </div>

      {/* Summary cards — pattern counts (4-quadrant matrix) — premium only */}
      {isPremium && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <KpiMini label="Stocks in F&O" value={data?.count ?? 0} accent="text-primary-fg" />
          <KpiMini label="Long Build-up" value={patternCounts["Long Build-up"]} accent="text-pos" icon={<TrendUp size={12} weight="bold" />} />
          <KpiMini label="Short Covering" value={patternCounts["Short Covering"]} accent="text-pos" icon={<Fire size={12} weight="fill" />} />
          <KpiMini label="Short Build-up" value={patternCounts["Short Build-up"]} accent="text-neg" icon={<TrendDown size={12} weight="bold" />} />
          <KpiMini label="Long Unwinding" value={patternCounts["Long Unwinding"]} accent="text-neg" icon={<TrendDown size={12} weight="bold" />} />
        </div>
      )}

      {/* Pattern filter chips — premium only */}
      {isPremium && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mr-1">Filter:</span>
        {[
          { k: "all", label: "All", cls: "" },
          { k: "bullish", label: "🟢 Bullish (Long↑ + Cover↑)", cls: "text-pos" },
          { k: "bearish", label: "🔴 Bearish (Short↓ + Unwind↓)", cls: "text-neg" },
          { k: "Long Build-up", label: "Long Build-up", cls: "text-pos" },
          { k: "Short Covering", label: "Short Covering", cls: "text-pos" },
          { k: "Short Build-up", label: "Short Build-up", cls: "text-neg" },
          { k: "Long Unwinding", label: "Long Unwinding", cls: "text-neg" },
        ].map(({ k, label, cls }) => (
          <button
            key={k}
            type="button"
            onClick={() => setPatternFilter(k)}
            data-testid={`pattern-filter-${k.replace(/\s+/g, "-").toLowerCase()}`}
            className={
              "px-2.5 py-1 border rounded-sm text-[10px] font-mono uppercase tracking-widest transition " +
              (patternFilter === k
                ? "bg-mod-soft border-mod-soft text-primary-fg"
                : `border-subtle hover:bg-surface-hover ${cls || "text-secondary-fg"}`)
            }
          >
            {label}
          </button>
        ))}
        </div>
      )}

      {/* Search */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 max-w-md px-3 py-2 border border-subtle rounded-sm bg-app">
          <MagnifyingGlass size={14} className="text-muted-fg" />
          <input
            data-testid="fno-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search symbol (e.g. RELIANCE)"
            className="w-full bg-transparent outline-none text-sm font-mono text-primary-fg placeholder:text-muted-fg"
          />
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
          showing <span className="text-primary-fg">{rows.length}</span> / {data?.count ?? 0}
        </div>
      </div>

      {/* Table */}
      <div className="border border-subtle rounded-sm overflow-hidden bg-surface">
        {isPremium ? (
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-subtle bg-app/50 text-muted-fg">
          <div className="col-span-2"><SortCell label="Symbol" k="symbol_dummy" align="left" /></div>
          <div className="col-span-2"><SortCell label="LTP" k="ltp" /></div>
          <div className="col-span-1"><SortCell label="% Δ Price" k="pct_change_price" /></div>
          <div className="col-span-2"><SortCell label="OI" k="oi" /></div>
          <div className="col-span-1"><SortCell label="Δ OI" k="change_oi" /></div>
          <div className="col-span-2"><SortCell label="% Δ OI" k="pct_change_oi" /></div>
          <div className="col-span-2 text-right"><span className="text-[10px] font-mono uppercase tracking-widest">Pattern</span></div>
        </div>
        ) : (
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-subtle bg-app/50 text-muted-fg">
          <div className="col-span-6"><SortCell label="Symbol" k="symbol_dummy" align="left" /></div>
          <div className="col-span-6"><SortCell label="% Δ OI" k="pct_change_oi" /></div>
        </div>
        )}

        <div className="max-h-[70vh] overflow-y-auto">
          {rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-fg">No symbols match.</div>
          ) : (
            rows.map((r, i) => {
              const bStyle = BUCKETS[r.bucket] || BUCKETS.Flat;
              const pos = r.pct_change_oi >= 0;
              const pricePos = (r.pct_change_price || 0) >= 0;
              const clickable = typeof onOpenStock === "function" && isPremium;
              return (
                <motion.div
                  key={r.symbol}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, delay: Math.min(0.005 * i, 0.15) }}
                  onClick={() => clickable && onOpenStock({
                    symbol: `${r.symbol}.NS`,
                    company_name: r.symbol,
                    // F&O rows have only LTP / % Δ price — no breakout plan.
                    // Pass them through so the modal shows live price instead
                    // of the placeholder "₹—" and avoids the bogus
                    // "+undefined% vs breakout" line.
                    last_price: r.ltp,
                    pct_change_price: r.pct_change_price,
                    fno_only: true,
                  })}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (clickable && (e.key === "Enter" || e.key === " ")) {
                      onOpenStock({
                        symbol: `${r.symbol}.NS`,
                        company_name: r.symbol,
                        last_price: r.ltp,
                        pct_change_price: r.pct_change_price,
                        fno_only: true,
                      });
                    }
                  }}
                  className={
                    "relative grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-subtle last:border-b-0 transition group " +
                    (clickable ? "cursor-pointer hover:bg-surface-hover" : "hover:bg-surface-hover")
                  }
                  data-testid={`fno-row-${r.symbol}`}
                >
                  <div
                    className="absolute left-0 top-0 bottom-0 w-0.5"
                    style={{ background: bStyle.row }}
                  />
                  {isPremium ? (
                    <>
                      <div className="col-span-2 font-mono text-sm font-semibold text-primary-fg tracking-wide truncate flex items-center gap-1">
                        {r.symbol}
                        {clickable && (
                          <span className="text-muted-fg text-[9px] opacity-0 group-hover:opacity-100 transition">↗</span>
                        )}
                      </div>
                      <div className="col-span-2 text-right font-mono text-sm tabular-nums text-primary-fg">
                        ₹{fmtNum(r.ltp)}
                      </div>
                      <div
                        className={
                          "col-span-1 text-right font-mono text-xs tabular-nums flex items-center justify-end gap-1 " +
                          (pricePos ? "text-pos" : "text-neg")
                        }
                      >
                        {pricePos ? "+" : ""}
                        {(r.pct_change_price || 0).toFixed(2)}%
                      </div>
                      <div className="col-span-2 text-right font-mono text-sm tabular-nums text-secondary-fg">
                        {fmtInt(r.oi)}
                      </div>
                      <div
                        className={
                          "col-span-1 text-right font-mono text-xs tabular-nums " +
                          (pos ? "text-pos" : "text-neg")
                        }
                      >
                        {r.change_oi >= 0 ? "+" : ""}
                        {fmtInt(r.change_oi)}
                      </div>
                      <div
                        className={
                          "col-span-2 text-right font-mono text-sm tabular-nums flex items-center justify-end gap-1 " +
                          (pos ? "text-pos" : "text-neg")
                        }
                      >
                        {pos ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
                        {pos ? "+" : ""}
                        {r.pct_change_oi.toFixed(2)}%
                      </div>
                      <div className="col-span-2 text-right">
                        <span
                          className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest rounded-sm border ${(PATTERNS[r.pattern] || PATTERNS.Neutral).chip}`}
                          title={r.pattern}
                        >
                          {(PATTERNS[r.pattern] || PATTERNS.Neutral).short}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="col-span-6 font-mono text-sm font-semibold text-primary-fg tracking-wide truncate">
                        {r.symbol}
                      </div>
                      <div
                        className={
                          "col-span-6 text-right font-mono text-sm tabular-nums flex items-center justify-end gap-1 " +
                          (pos ? "text-pos" : "text-neg")
                        }
                      >
                        {pos ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
                        {pos ? "+" : ""}
                        {r.pct_change_oi.toFixed(2)}%
                      </div>
                    </>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      </div>

      <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
        data source: nseindia.com · live-analysis-oi-spurts · not investment advice
      </div>
    </div>
  );
}

function KpiMini({ label, value, accent = "text-primary-fg", icon }) {
  return (
    <div className="p-3 border border-subtle rounded-sm bg-surface">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className={`font-mono text-xl tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}
